// @ts-strict-ignore
import { useTheme } from "@emotion/react";
import { faMoon, faSunBright } from "@fortawesome/pro-regular-svg-icons";
import { useTranslation } from "react-i18next";

import { l } from "@aviary/locales/i18n";
import type { ThemeTypes } from "@aviary/utils/AviaryThemeContext";
import { useRudderStackAnalytics } from "@shared/components/Rudderstack/InitializeRudderstack/useRudderAnalytics";
import { useIdentify } from "@shared/components/Rudderstack/useIdentify/useIdentify";
import { AnalyticsEvent } from "@shared/hooks/useAnalyticsLogger/AnalyticsEvent";
import { useAnalyticsLogger } from "@shared/hooks/useAnalyticsLogger/useAnalyticsLogger";
import { useAppcues } from "@shared/hooks/useAppcues/useAppcues";
import { useIdentifyTraitsQuery } from "@shared/hooks/useIdentifyTraitsData/IdentifyTraits.query";
import { useIdentifyTraitsData } from "@shared/hooks/useIdentifyTraitsData/useIdentifyTraitsData";
import { useSharedGlobalConfig } from "@shared/hooks/useSharedGlobalConfig/useSharedGlobalConfig";

import { useSetAviaryTheme } from "../../utils/useSetAviaryTheme";
import { Toggle } from "../Toggle";

import * as styles from "./ThemeToggle.styles";

const ThemeToggle = () => {
  const { t } = useTranslation();
  const logAnalyticsEvent = useAnalyticsLogger();

  const { isPatient } = useSharedGlobalConfig();
  const isAppcuesEnabled = useAppcues();
  const analytics = useRudderStackAnalytics(isAppcuesEnabled);
  const { data, loading } = useIdentifyTraitsQuery();
  const getTraitsData = useIdentifyTraitsData();
  const identify = useIdentify(isAppcuesEnabled);

  const { setTheme } = useSetAviaryTheme();
  const currentTheme = useTheme();
  const isDarkMode = currentTheme.theme === "dark";

  const handleToggle = () => {
    logAnalyticsEvent({
      eventName: isPatient
        ? AnalyticsEvent.PATIENT_THEME_DARK_MODE_TOGGLE
        : AnalyticsEvent.STORE_THEME_DARK_MODE_TOGGLE,
      payload: {
        theme_target: isDarkMode ? "light" : "dark",
      },
    });

    let theme: ThemeTypes = "dark";
    if (isDarkMode) {
      theme = "light";
    }
    setTheme(theme);

    if (isAppcuesEnabled) {
      identify(analytics, getTraitsData(data, loading, theme));
    }
  };

  return (
    <Toggle
      isOn={isDarkMode}
      onClick={handleToggle}
      onLabel={t(l.aviary.themeToggle.darkMode)}
      offLabel={t(l.aviary.themeToggle.lightMode)}
      icon={isDarkMode ? faMoon : faSunBright}
      css={styles.label}
    />
  );
};

export { ThemeToggle };
